import { Suspense, lazy } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useApiClients } from '@/app/ApiProvider';
import { useCapability } from '@/app/CapabilitiesProvider';
import { orgRoute } from '@/app/org-route';
import { Route as SettingsRoute } from '@/routes/$orgId.settings';

// Each section is lazy-loaded so the Settings shell doesn't pull in
// every admin screen on first paint. Keeps the admin chunk under
// budget (the unified shell would otherwise eagerly import S13–S16
// + SAuditLog whether the user opens that section or not).
const S11Settings = lazy(() =>
  import('@/screens/S11Settings/S11Settings').then((m) => ({ default: m.S11Settings })),
);
const S13Users = lazy(() =>
  import('@/screens/S13Users/S13Users').then((m) => ({ default: m.S13Users })),
);
const S14Groups = lazy(() =>
  import('@/screens/S14Groups/S14Groups').then((m) => ({ default: m.S14Groups })),
);
const S15Roles = lazy(() =>
  import('@/screens/S15Roles/S15Roles').then((m) => ({ default: m.S15Roles })),
);
const S16OrgSettings = lazy(() =>
  import('@/screens/S16OrgSettings/S16OrgSettings').then((m) => ({ default: m.S16OrgSettings })),
);
const SAuditLog = lazy(() =>
  import('@/screens/SAuditLog/SAuditLog').then((m) => ({ default: m.SAuditLog })),
);

// Single Settings destination, cap-gated left rail. Replaces the old
// AdminMenu disclosure in the header. Per ADR 0010 the items every
// role gets vs admin-only items are determined by capabilities, not
// by role name — so a manager who happens to carry user.invite +
// group.manage sees those two Workspace sections; a maintainer sees
// only Account.
//
// IA principles applied here:
//   - one umbrella for all configuration ("Settings")
//   - scales with role naturally (no "this menu is empty for you")
//   - removes the duplicate Org settings that used to live in both
//     the AdminMenu and the UserMenu
//   - clears the header's right cluster to Search + UserMenu

type SectionKey =
  | 'profile'
  | 'users'
  | 'groups'
  | 'roles'
  | 'org'
  | 'audit';

const GROUPS: Array<{
  label: string;
  sections: Array<{ key: SectionKey; label: string }>;
}> = [
  {
    label: 'Account',
    sections: [{ key: 'profile', label: 'Profile & theme' }],
  },
  {
    label: 'Workspace',
    sections: [
      { key: 'users', label: 'Users' },
      { key: 'groups', label: 'Groups' },
      { key: 'roles', label: 'Roles & policy' },
      { key: 'org', label: 'Organization' },
      { key: 'audit', label: 'Audit log' },
    ],
  },
];

export function SSettings() {
  const navigate = useNavigate();
  const clients = useApiClients();
  const search = SettingsRoute.useSearch();

  // Call useCapability once per known cap at the top level so React's
  // rules-of-hooks invariant holds across renders.
  const canInvite = useCapability('user.invite');
  const canManageGroups = useCapability('group.manage');
  const canEditPolicy = useCapability('org.edit_policy');
  const canEditOrg = useCapability('org.edit');
  const canViewAudit = useCapability('org.audit_log');

  const visibility: Record<SectionKey, boolean> = {
    profile: true,
    users: canInvite,
    groups: canManageGroups,
    roles: canEditPolicy,
    org: canEditOrg,
    audit: canViewAudit,
  };

  const visibleGroups = GROUPS.map((g) => ({
    ...g,
    sections: g.sections.filter((s) => visibility[s.key]),
  })).filter((g) => g.sections.length > 0);

  const allKeys = visibleGroups.flatMap((g) => g.sections.map((s) => s.key));
  const requested = (search.section ?? '') as SectionKey;
  const activeKey: SectionKey = allKeys.includes(requested)
    ? requested
    : allKeys[0] ?? 'profile';

  const goTo = (key: SectionKey) =>
    navigate({ to: orgRoute('/settings'), search: { section: key }, replace: true });

  let body: React.ReactElement;
  switch (activeKey) {
    case 'profile':
      body = <S11Settings />;
      break;
    case 'users':
      body = <S13Users clients={clients} />;
      break;
    case 'groups':
      body = <S14Groups clients={clients} />;
      break;
    case 'roles':
      body = <S15Roles clients={clients} />;
      break;
    case 'org':
      body = <S16OrgSettings clients={clients} />;
      break;
    case 'audit':
      body = <SAuditLog clients={clients} />;
      break;
  }

  return (
    <div className="grid gap-sp-6 lg:grid-cols-[240px_1fr]">
      <aside aria-label="Settings sections">
        {/* No "Settings" heading here — each embedded section has its
            own h1. Duplicating it in the rail caused a "Settings /
            Settings" stack on first load. The active rail button +
            URL serve as the breadcrumb. */}
        <p className="mb-sp-4 text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
          Settings
        </p>
        <nav className="flex flex-col gap-sp-4">
          {visibleGroups.map((g) => (
            <div key={g.label} className="flex flex-col gap-sp-1">
              <p className="px-sp-2 text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
                {g.label}
              </p>
              <ul className="flex flex-col">
                {g.sections.map((s) => {
                  const isActive = activeKey === s.key;
                  return (
                    <li key={s.key}>
                      <button
                        type="button"
                        aria-current={isActive ? 'page' : undefined}
                        onClick={() => goTo(s.key)}
                        className={
                          'block w-full rounded-r-default px-sp-2 py-sp-2 text-left text-fs-body transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-info ' +
                          (isActive
                            ? 'bg-gray-900 text-gray-50'
                            : 'text-gray-900 hover:bg-gray-100')
                        }
                      >
                        {s.label}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>
      </aside>

      <section aria-label="Settings content">
        <Suspense
          fallback={<p className="text-fs-body text-gray-700">Loading…</p>}
        >
          {body}
        </Suspense>
      </section>
    </div>
  );
}
